import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  Add as AddIcon,
  Group as GroupIcon,
} from '@mui/icons-material';

type CollaborationAddFormsProps = {
  isCurrentUserOwner: boolean;
  newCollabEmail: string;
  setNewCollabEmail: (val: string) => void;
  newReaderEmail: string;
  setNewReaderEmail: (val: string) => void;
  collabError: string | null;
  readerError: string | null;
  isUpdatingCollab: boolean;
  isUpdatingReader: boolean;
  onAddCollaborator: () => Promise<void>;
  onAddReader: () => Promise<void>;
};

export function CollaborationAddForms({
  isCurrentUserOwner,
  newCollabEmail,
  setNewCollabEmail,
  newReaderEmail,
  setNewReaderEmail,
  collabError,
  readerError,
  isUpdatingCollab,
  isUpdatingReader,
  onAddCollaborator,
  onAddReader,
}: CollaborationAddFormsProps) {
  const { t } = useTranslation();

  if (!isCurrentUserOwner) {
    return (
      <Box
        sx={{
          p: 4,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'action.hover',
        }}
      >
        <GroupIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('dashboard.settings.read_only_access')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
          {t('dashboard.settings.read_only_access_desc')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ height: '100%' }}>
      {/* Add Collaborator Form */}
      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'primary.light',
          background: (theme) => alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon />
          {t('dashboard.settings.add_collaborator')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {t('dashboard.settings.collaborator_desc')}
        </Typography>

        <Stack spacing={2}>
          <TextField
            size="small"
            fullWidth
            label={t('dashboard.settings.user_query_label')}
            placeholder="e.g. writer@eplotera.com or JohnDoe"
            value={newCollabEmail}
            onChange={(e) => setNewCollabEmail(e.target.value)}
            disabled={isUpdatingCollab}
            error={Boolean(collabError)}
            helperText={collabError}
            sx={{ bgcolor: 'background.paper' }}
          />
          <Button
            variant="contained"
            onClick={onAddCollaborator}
            disabled={isUpdatingCollab || !newCollabEmail.trim()}
            startIcon={isUpdatingCollab ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{
              borderRadius: 2,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              color: 'primary.contrastText',
              boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
              '&:hover': {
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              }
            }}
          >
            {isUpdatingCollab ? t('dashboard.settings.adding') : t('dashboard.settings.add_collaborator_btn')}
          </Button>
        </Stack>
      </Box>

      {/* Add Reader Form */}
      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'secondary.light',
          background: (theme) => alpha(theme.palette.secondary.main, 0.03),
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'secondary.light', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon />
          {t('dashboard.settings.add_reader')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {t('dashboard.settings.reader_desc')}
        </Typography>

        <Stack spacing={2}>
          <TextField
            size="small"
            fullWidth
            label={t('dashboard.settings.user_query_label_reader')}
            placeholder="e.g. reader@eplotera.com or JaneDoe"
            value={newReaderEmail}
            onChange={(e) => setNewReaderEmail(e.target.value)}
            disabled={isUpdatingReader}
            error={Boolean(readerError)}
            helperText={readerError}
            sx={{ bgcolor: 'background.paper' }}
          />
          <Button
            variant="contained"
            onClick={onAddReader}
            disabled={isUpdatingReader || !newReaderEmail.trim()}
            startIcon={isUpdatingReader ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{
              borderRadius: 2,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
              color: '#ffffff',
              boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`,
              '&:hover': {
                background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
              }
            }}
          >
            {isUpdatingReader ? t('dashboard.settings.adding') : t('dashboard.settings.add_reader_btn')}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
